import { inr, inrFull, num, pct } from "../../lib/format";
import { SectionTitle } from "../kit";

const Tile = ({ label, value, sub, tone = "text-[#011E38]", testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="text-[0.7rem] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}</div>
    <div className={`num text-2xl font-bold mt-1.5 ${tone}`} data-testid={testid && `${testid}-value`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

export function GmsCards({ s }) {
  const p = s.payments, r = s.reenroll, u = s.upsell;
  return (
    <div className="space-y-8" data-testid="gms-summary">
      <section>
        <SectionTitle eyebrow="Payment tracker · by due date" title="Upcoming Payments" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Tile label={`Next ${s.window_days} days`} value={num(p.upcoming.count)} sub={inrFull(p.upcoming.value)} testid="gms-upcoming" />
          <Tile label="Due Today" value={num(p.due_today.count)} sub={inrFull(p.due_today.value)} tone="text-amber-700" testid="gms-due-today" />
          <Tile label="Overdue" value={num(p.overdue.count)} sub={inrFull(p.overdue.value)} tone="text-red-700" testid="gms-overdue" />
          <Tile label="Paid in period" value={num(s.paid_in_period.count)} sub={inrFull(s.paid_in_period.value)} tone="text-green-700" testid="gms-paid-period" />
        </div>
      </section>
      <section>
        <SectionTitle eyebrow="Selected period" title="Re-enrollment" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Tile label="Matured Customers" value={num(r.matured)} testid="gms-reenroll-matured" />
          <Tile label="Re-enrolled" value={num(r.count)} tone="text-green-700" testid="gms-reenroll-count" />
          <Tile label="Not Re-enrolled" value={num(r.not_reenrolled)} tone="text-red-700" testid="gms-reenroll-not" />
          <Tile label="Conversion %" value={pct(r.conversion_pct, 0)} sub="re-enrolled ÷ matured" testid="gms-reenroll-conversion" />
          <Tile label="Total New GMS" value={inr(r.new_value)} sub={`from ${inr(r.prev_value)}`} testid="gms-reenroll-new" />
          <Tile label="Avg Re-enrollment %" value={pct(r.avg_pct, 0)} sub="re-enrolled customers only" testid="gms-reenroll-pct" />
          <Tile label="Increase Value" value={inr(r.increase)} testid="gms-reenroll-increase" />
          <Tile label="Increase %" value={pct(r.increase_pct, 0)} testid="gms-reenroll-increase-pct" />
        </div>
      </section>
      <section>
        <SectionTitle eyebrow="Selected period" title="Upselling" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Tile label="Upsellings" value={num(u.count)} testid="gms-upsell-count" />
          <Tile label="Matured GMS" value={inr(u.matured_value)} testid="gms-upsell-matured" />
          <Tile label="Jewellery Sale" value={inr(u.sale_value)} testid="gms-upsell-sale" />
          <Tile label="Upselling Value" value={inr(u.upsell_value)} tone="text-green-700" testid="gms-upsell-value" />
          <Tile label="Upselling %" value={pct(u.upsell_pct, 0)} testid="gms-upsell-pct" />
        </div>
      </section>
    </div>
  );
}

export function GmsMetricTable({ s }) {
  const rows = [["Re-enrollment", s.reenroll.count, s.reenroll.new_value, `${s.reenroll.count}/${s.reenroll.matured} matured · ${pct(s.reenroll.conversion_pct, 0)} conversion · +${inr(s.reenroll.increase)}`],
    ["Upselling", s.upsell.count, s.upsell.upsell_value, pct(s.upsell.upsell_pct, 0)]];
  return (
    <div className="bs-card overflow-x-auto" data-testid="gms-metric-table">
      <table className="w-full text-left">
        <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100"><tr><th className="px-5 py-3">Metric</th><th className="px-5 text-right">Count</th><th className="px-5 text-right">Value</th><th className="px-5 text-right">Change</th></tr></thead>
        <tbody>{rows.map(([l, c, v, x]) => (
          <tr key={l} className="border-t border-slate-50" data-testid={`gms-metric-${l.toLowerCase()}`}><td className="px-5 py-4 font-semibold text-[#011E38]">{l}</td><td className="px-5 text-right num text-lg">{c}</td><td className="px-5 text-right num text-lg font-bold">{inrFull(v)}</td><td className="px-5 text-right text-sm text-slate-500">{x}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export function GmsBreakdown({ rows, label, testid }) {
  if (!rows?.length) return null;
  return (
    <div className="bs-card overflow-x-auto" data-testid={testid}>
      <table className="w-full text-left text-sm min-w-[720px]">
        <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
          <tr><th className="px-5 py-3">{label}</th><th className="px-3 text-right">Re-enrolled / Matured</th><th className="px-3 text-right">Conversion</th><th className="px-3 text-right">New GMS</th><th className="px-3 text-right">Increase</th><th className="px-3 text-right">Upsell</th><th className="px-3 text-right">Upsell Value</th><th className="px-3 text-right">Upcoming</th><th className="px-3 text-right">Due Today</th><th className="px-5 text-right">Overdue</th></tr>
        </thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id} className="border-t border-slate-50" data-testid={`${testid}-row-${r.employee_id || r.id}`}>
            <td className="px-5 py-3.5 font-semibold text-[#011E38]">{r.name}{r.employee_id && <div className="text-xs text-slate-500 font-normal">{r.employee_id}</div>}</td>
            <td className="px-3 text-right num">{r.reenroll.count}/{r.reenroll.matured}</td>
            <td className="px-3 text-right num">{pct(r.reenroll.conversion_pct, 0)}</td>
            <td className="px-3 text-right num">{inr(r.reenroll.new_value)}</td>
            <td className="px-3 text-right num">{inr(r.reenroll.increase)}</td>
            <td className="px-3 text-right num">{r.upsell.count}</td>
            <td className="px-3 text-right num font-semibold">{inr(r.upsell.upsell_value)}</td>
            <td className="px-3 text-right num">{r.payments.upcoming.count} · {inr(r.payments.upcoming.value)}</td>
            <td className="px-3 text-right num text-amber-700">{r.payments.due_today.count}</td>
            <td className="px-5 text-right num text-red-700">{r.payments.overdue.count} · {inr(r.payments.overdue.value)}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}
